import styles from "./SearchCard.module.css"
import { useRef, useState } from 'react';
import { Minus, Plus } from 'lucide-react';
import { getPrimaryProductImage } from "../../../services/productImageService";
import { useOrderContext } from "../../../context/OrderContext";
import { useDetailContext } from '../../../context/DetailContext';
import { usePreferences } from "../../../context/PreferencesContext";
import { czk_eur_rate } from "../../../constants/prices";
import { czk_to_eur, formatCurrency } from "../../../utils/prices";
import { useTranslation } from "react-i18next";
import { ProductCardProps } from "../../../types/product.types";
import { normalizeDate, denormalizeDate } from "../../../utils/calendar";
import { useSearchWindow } from "../../../context/SearchContext";
import { useCartContext } from "../../../context/CartContext";
import Tooltip from "../../Tooltip/Tooltip";
import useTimeModalActions from "../../../hooks/useTimeModalActions";
import CartButtonSecondary from "../../Button/CartButton/CartButtonSecondary";
import { defaultOrderTime } from "../../../constants/times";
import { usePublicCatalog } from "../../../context/PublicCatalogContext";
import useCartAmountSync from "../../../hooks/useCartAmountSync";
import useCardClickGuard from "../../../hooks/useCardClickGuard";
import { getAdvanceDays, getDeliveryWindow } from "../../../utils/products";
import { getQuantityTooltip } from "../../../utils/quantity";
interface SearchCardProps extends ProductCardProps{
    search: string
}
/**
* Search card for products
*
* @param props SearchCardProps
* @param {Product} props.product - Product
* @param {search} props.search - search value
* @returns TSX.Element
*/
const SearchCard = ({ product, search, type = "sales" } : SearchCardProps) => {    
    const { cartData, reloadCart, cartLoaded } = useCartContext();
    const { t, i18n } = useTranslation(); // Translation
    const { openDetail } = useDetailContext(); // Detail context
    const { orderData } = useOrderContext(); // Order data
    const { selectedCurrency } = usePreferences(); // Curent selected currency
    const { close, setLast  } = useSearchWindow();
    const { openTimeModal } = useTimeModalActions(); // Time modal
    const {isPublicView} = usePublicCatalog();

    const normalizedType = type === "package" ? "package" : "sales";
    const dateKey = denormalizeDate(orderData.selectedDate);
    const dateISO = normalizeDate(orderData.selectedDate);
    const { cartItem: firstCartInstance, cartAmount, handleCart } = useCartAmountSync({
        cartData,
        cartLoaded,
        reloadCart,
        itemKey: {
            id: product.id,
            category: normalizedType,
            dateKey,
            dateISO,
        },
    });
    const no_dph_price = product.pricing?.price_without_tax != null ? czk_to_eur(product.pricing.price_without_tax , czk_eur_rate, selectedCurrency).toFixed(2) : "N/A" // Total price
    
    const buttonRef = useRef<HTMLDivElement>(null)
    const actionButtonsRef = useRef<HTMLDivElement>(null)
    // Handle opening detail
    const handleOpen = useCardClickGuard([buttonRef, actionButtonsRef], () => {
        setLast(search)
        close();
        openDetail && openDetail({id: product.id, type: normalizedType }, true); 
    });

    // Sample time limit
    const { min: minDelivery, max: maxDelivery } = getDeliveryWindow(product);
    // Order in advance
    const advance = getAdvanceDays(product);
    const minOrder = product.min_order ?? 1;
    const granularity = product.granularity ?? 1;
    const tooltip = getQuantityTooltip({
        cartAmount,
        minOrder,
        granularity,
        language: i18n.language,
        itemType: "product",
    });

    const [showTooltip, setShowTooltip] = useState(false);

    return(
        <>
        <div className={styles.SearchCard} key={product.id} onClick={handleOpen}>
            <div className={styles.SearchCardImage} style={{ backgroundImage: `url(${getPrimaryProductImage(product)})` }}></div>
            <div className={styles.SearchCardContent}>
                <div className={styles.SearchCardInfo}>
                    <p className={styles.SearchCardTitle}>{i18n.language === "en" ? product.name_en || "N/A" : product.name}</p>
                </div>
                <div className={styles.SearchCardOrderInfo}>

                            <div className={styles.SearchCardAmount} ref={buttonRef}>
                                {(cartAmount > 0 && !isPublicView) ?(
                                    <>
                                    {/*className={cartAmount == 1 ? `${styles.CartProductAmountDecrease} ${styles.CartProductAmountDisabled}` : `${styles.CartProductAmountDecrease}`*/}
                                        <button /*disabled={cartAmount <= product.min_order} */className={`${styles.SearchCardAmountDecrease}`} onClick={()=>{cartAmount > product.min_order ? handleCart(cartAmount - (1*(product.granularity??1))) : handleCart(0)}} disabled={ isPublicView}>
                                            <Minus/>
                                        </button>
                                        <div className={styles.SearchCardAmountText}>
                                            <p>{cartAmount}</p>
                                        </div>
                                        <button disabled={(cartAmount >= ((product.max_order === null || product.max_order === 0) ? 100 : product.max_order)) || isPublicView} className={styles.SearchCardAmountIncrease} onClick={()=>handleCart(cartAmount + (1*(product.granularity??1)))}>
                                            <Plus/>
                                        </button>
                                        {showTooltip && <Tooltip desc={tooltip} className={styles.Tooltips} targetRef={buttonRef}>{tooltip}</Tooltip>}
                                    </>
                                ):(
                                    <>
                                        <CartButtonSecondary
                                            colorVariant="primary"
                                            text={t("buttons.order")}
                                            onClick={() => {
                                                const baseAmount = Math.max(
                                                    product.min_order ?? 1,
                                                    product.granularity ?? 1
                                                );
                                                const safeAmount =
                                                    Number.isFinite(baseAmount) && baseAmount > 0
                                                        ? baseAmount
                                                        : 1;
                                                openTimeModal(
                                                    {
                                                        type: "single",
                                                        date: orderData.selectedDate,
                                                        time: defaultOrderTime,
                                                        productId: product.id,
                                                        prodType: normalizedType,
                                                        static: true,
                                                        amount: safeAmount,
                                                        restrictions: { min: minDelivery, max: maxDelivery },
                                                        advance: advance,
                                                    },
                                                    { openOnTop: true }
                                                );
                                            }}
                                            className={styles.DetailCartButton}
                                            disabled={isPublicView}
                                        />
                                    </>
                                )}
                            </div>
                            <p className={styles.SearchCardPrice}> {t("products.no_dph_short")} {formatCurrency(no_dph_price, selectedCurrency)}</p>
                </div>
            </div>
        </div>
        </>
    )
}   

export default SearchCard
